import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { updateEmployee, useEmployee } from '../Data/EmployeeData';
import TextField from '@mui/material/TextField';
import { EmployeeUpdate, IEmployee } from '../Interfaces/Admin/IEmployee';
import produce from 'immer';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import {
  Checkbox, List, ListItem, ListItemText, ListItemSecondaryAction, Typography
} from "@mui/material";
import MenuItem from '@mui/material/MenuItem';
import { useDispatch, useSelector } from 'react-redux';

import { IEmployeeRole } from '../Interfaces/Admin/IEmployeeRole';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
// import { TextMaskCustom } from './PhoneInput';
import { ApplicationDispatch, SET_LOADING, SET_SERVER_MESSAGE } from '../store/application/types';
import { includes, reject } from 'ramda';
import { PhoneInput, DatePickerWithYearSanitizer } from '@amsanalytics/react-shared-library';
import { AppState } from '../store/rootReducer';
import {Organization} from "@amsanalytics/react-shared-library/build/Models/Organization";
import {DatePickerChangeEvent} from '@progress/kendo-react-dateinputs';

export interface EmployeeEditProps {

}

export const EmployeeEdit: React.FC<EmployeeEditProps> = props => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const navigate = useNavigate();
  const params: Record<string, string | undefined> = useParams();
  const { data: employee, isFetching } = useEmployee(params.id);
  const organizations: Organization[] = useSelector((state: AppState) => state.applications.lookups.organizations);
  const employeeRoles: IEmployeeRole[] = useSelector((state: AppState) => state.applications.employeeRoles);
  const [employeeEdit, setEmployeeEdit] = useState<Partial<EmployeeUpdate> | undefined>();

  const getEmployeeToEdit = (employee: IEmployee | undefined): Partial<EmployeeUpdate> | undefined => {
    return employee
      ? {
        id: employee.id,
        firstName: employee.firstName,
        middleName: employee.middleName,
        lastName: employee.lastName,
        dateOfBirth: employee.dateOfBirth,
        emailAddress: employee.emailAddress,
        phoneNumber: employee.phoneNumber,
        adUserId: employee.adUserId,
        organizationId: employee.organizationId,
        employeeRoleIds: employee.employeeEmployeeRoles?.map(eer => eer.employeeRoleId) ?? []
      }
      : undefined;
  }

  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    setEmployeeEdit(getEmployeeToEdit(employee));
    if (!isFetching || !params.id) {
      setIsLoaded(true);
    }
  }, [employee]);

  const updateUserRoles = (employeeRole: IEmployeeRole, addRole: boolean) => {
    const result = produce(employeeEdit, employee => {

      if (addRole) {
        if (!includes(employeeRole.id, employee!.employeeRoleIds!)) {
          employee!.employeeRoleIds!.push(employeeRole.id);
        }
      } else {
        employee!.employeeRoleIds = reject(erId => erId === employeeRole.id, employee!.employeeRoleIds!);
      }
    });
    setEmployeeEdit(result);
  }

  return (
    employeeEdit ? (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flex: 1 }}>
            <Grid container spacing={3} style={{ padding: 24, width: '100%' }}>
              <Grid item xs={6} md={3}>
                <TextField 
                  label="AD User ID"
                  style={{minWidth:'150px'}}
                  InputLabelProps={{ shrink: true }}
                  required 
                  disabled
                  fullWidth
                  value={employeeEdit?.adUserId}
                  onChange={(e) => {
                    setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                      employeeEdit.adUserId = e.target.value;
                    }));
                  }}
                  error={isLoaded && !employeeEdit.adUserId}
                  helperText={isLoaded && !employeeEdit.adUserId && 'Required'}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Email"
                  InputLabelProps={{ shrink: true }}
                  style={{minWidth:'150px'}}
                  required
                  fullWidth
                  value={employeeEdit?.emailAddress}
                  onChange={(e) => {
                    setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                      employeeEdit.emailAddress = e.target.value;
                    }));
                  }}
                  error={isLoaded && !employeeEdit.emailAddress}
                  helperText={isLoaded && !employeeEdit.emailAddress && 'Required'}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Location"
                  select
                  required
                  style={{minWidth:'150px'}}
                  fullWidth
                  value={employeeEdit.organizationId}
                  onChange={(e) => {
                    setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                      employeeEdit.organizationId = e.target.value;
                    }));
                  }}
                  error={isLoaded && !employeeEdit.organizationId}
                  helperText={isLoaded && !employeeEdit.organizationId && 'Required'}
                >
                  {organizations.map((organization, key) =>
                    <MenuItem key={key} value={organization.id}>
                      {organization.name}
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="First Name"
                  InputLabelProps={{ shrink: true }}
                  style={{minWidth:'150px'}}
                  required
                  fullWidth
                  value={employeeEdit?.firstName}
                  onChange={(e) => {
                    setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                      employeeEdit.firstName = e.target.value;
                    }));
                  }}
                  error={isLoaded && !employeeEdit.firstName}
                  helperText={isLoaded && !employeeEdit.firstName && 'Required'}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Middle Name"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  style={{minWidth:'150px'}}
                  value={employeeEdit?.middleName}
                  onChange={(e) => {
                    setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                      employeeEdit.middleName = e.target.value;
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Last Name"
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                  style={{minWidth:'150px'}}
                  value={employeeEdit?.lastName}
                  onChange={(e) => {
                    setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                      employeeEdit.lastName = e.target.value;
                    }));
                  }}
                  error={isLoaded && !employeeEdit.lastName}
                  helperText={isLoaded && !employeeEdit.lastName && 'Required'}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                  <DatePickerWithYearSanitizer
                    label="Date of Birth"
                    value={employeeEdit.dateOfBirth ? new Date(employeeEdit.dateOfBirth) : null}
                    onChange={(event: DatePickerChangeEvent) => {
                      setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                        employeeEdit.dateOfBirth = event.value?.toString() || '';
                      }));
                    }}
                  />
              </Grid>
              <Grid item xs={6} md={3}>
                {/* <TextField label="Phone Number"
              fullWidth
              value={employeeEdit?.phoneNumber}
              onChange={(e) => {
                setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                  employeeEdit.phoneNumber = e.target.value;
                }));
              }}
              InputProps={{
                inputComponent: TextMaskCustom as any
              }}
              InputLabelProps={{
                shrink: true,
              }}
            /> */}
                <PhoneInput
                  label='Phone Number'
                  style={{ backgroundColor: 'white', width: '100%' }}
                  value={employeeEdit?.phoneNumber || ''}
                  onChangeValue={(value: string) => {
                    setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                      employeeEdit.phoneNumber = value;
                    }));
                  }}
                />
              </Grid>
            </Grid>
          </div>
          <div style={{ width: '350px', marginTop: '20px', marginRight: '20px' }}>
            <Typography variant="caption">Roles</Typography>
            <List dense style={{
              marginTop: 0,
              paddingTop: 0,
              height: 250,
              border: 'solid .5px #d0d0ff', padding: '5px',
              overflowY: "scroll"
            }}>
              {employeeRoles && employeeRoles.map((role: IEmployeeRole, key: number) => {
                const labelId = `roles-label-${key}`;
                return (
                  <ListItem key={key} component="button">
                    <ListItemText id={labelId} primary={role.name} />
                    <ListItemSecondaryAction>
                      <Checkbox
                        edge="end"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                          updateUserRoles(role, checked);
                        }}
                        checked={includes(role.id, employeeEdit.employeeRoleIds!)}
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginRight: '20px', marginTop: '20px' }}>
          <FormControlLabel
            label="Active"
            control={
              <Switch
                checked={!employeeEdit.deleted}
                onChange={() => {
                  setEmployeeEdit(produce(employeeEdit, employeeEdit => {
                    employeeEdit.deleted = !employeeEdit.deleted;
                  }))
                }}
                color="primary"
              />
            }
          />
          <Button variant="contained"
            color="primary"
            onClick={async () => {
              try {
                dispatch({ type: SET_LOADING, payload: true });
                await updateEmployee(employeeEdit as EmployeeUpdate);
                navigate(-1);
              } catch (error) {
                dispatch({
                  type: SET_SERVER_MESSAGE,
                  payload: {
                    message: error as string,
                    isError: true,
                  },
                });
              } finally {
                dispatch({ type: SET_LOADING, payload: false });
              }
            }}
            disabled={!isLoaded}
          >Save</Button>
          <Button variant="contained"
            style={{ marginLeft: 10 }}
            onClick={() => {
              navigate(-1);
            }}
          >Cancel</Button>
        </div>
      </div>
    )
      :
      <div></div>
  );
}
